import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const sections = [
  { id: 'overview', label: 'Overview', icon: 'ri-compass-line' },
  { id: 'content-standards', label: 'Content Standards', icon: 'ri-file-text-line' },
  { id: 'writer-rules', label: 'Writer Rules', icon: 'ri-quill-pen-line' },
  { id: 'community-conduct', label: 'Community Conduct', icon: 'ri-group-line' },
  { id: 'moderation', label: 'Moderation', icon: 'ri-shield-line' },
  { id: 'faq', label: 'FAQ', icon: 'ri-question-line' },
];

const allowedContent = [
  'Psychological horror and slow-burn dread',
  'Supernatural and paranormal fiction',
  'Cosmic horror and existential terror',
  'Slasher and survival horror narratives',
  'Creepypasta and found-footage style writing',
  'Gothic and atmospheric horror',
  'Body horror and visceral terror (within taste guidelines)',
  'Horror with dark humour undertones',
];

const prohibitedContent = [
  'Sexual content involving minors — zero tolerance, immediate ban',
  'Non-consensual scenarios presented approvingly or erotically',
  'Real-world harassment disguised as fiction',
  'Targeted content designed to harm real, named individuals',
  'Hate speech based on race, religion, gender, sexuality, or disability',
  'Gratuitous gore with no narrative purpose',
  'Plagiarised work — all submissions must be original',
];

const writerRules = [
  {
    icon: 'ri-user-line',
    title: 'Originality Required',
    body: 'Every story submitted must be your own original work. Plagiarism of any kind (including paraphrasing another author\'s work without attribution) will result in immediate removal and a permanent ban.',
  },
  {
    icon: 'ri-translate-2',
    title: 'English Only',
    body: 'At this time, Sinister Files only accepts stories written in English. We hope to expand to other languages in the future, but for now all submissions must be in English.',
  },
  {
    icon: 'ri-bar-chart-line',
    title: 'Word Count',
    body: 'Stories should be a minimum of 500 words. There is no hard upper limit, but exceptionally long submissions (100,000+ words) may be split into chapters by the editorial team.',
  },
  {
    icon: 'ri-bookmark-line',
    title: 'Genre Tagging',
    body: 'Accurately tag your story with the appropriate genre(s). Misleading tags, such as tagging a romance story as horror, will result in removal and may affect your standing on the platform.',
  },
  {
    icon: 'ri-warning-line',
    title: 'Content Warnings',
    body: 'If your story contains graphic violence, disturbing themes, or mature content, you must include a content warning at the start of the story body. Readers deserve the right to make informed choices.',
  },
  {
    icon: 'ri-edit-line',
    title: 'Editing After Publishing',
    body: 'You may edit your story after publishing. However, making substantial changes to a story that has already accumulated significant ratings may reset its ranking. Minor corrections are always welcome.',
  },
];

const conductRules = [
  {
    level: 'green',
    title: 'Encouraged',
    icon: 'ri-thumb-up-line',
    items: [
      'Constructive criticism delivered with kindness',
      'Celebrating writers you enjoy',
      'Sharing theories and analysis of stories',
      'Welcoming new members to the community',
      'Reporting content that violates guidelines',
    ],
  },
  {
    level: 'yellow',
    title: 'Tread Carefully',
    icon: 'ri-alert-line',
    items: [
      'Debating dark themes in fiction (keep it civil)',
      'Expressing strong negative opinions of a story (be respectful)',
      'Horror sub-genre tribalism, we love all kinds of dark fiction',
      'Graphic descriptions in forum posts (use spoiler tags)',
    ],
  },
  {
    level: 'red',
    title: 'Prohibited',
    icon: 'ri-close-circle-line',
    items: [
      'Personal attacks or harassment of any member',
      'Doxxing, sharing private information about others',
      'Coordinated vote manipulation on stories or comments',
      'Spam, self-promotion flooding, or off-topic advertising',
      'Impersonating other members or the moderation team',
    ],
  },
];

const moderationSteps = [
  {
    step: '01',
    title: 'Warning',
    description: 'First-time minor infractions result in a formal warning. The offending content is removed and the member is notified with the specific rule violated.',
  },
  {
    step: '02',
    title: 'Temporary Suspension',
    description: 'Repeated violations or a single serious infraction result in a temporary account suspension ranging from 7 to 30 days depending on severity.',
  },
  {
    step: '03',
    title: 'Permanent Ban',
    description: 'Severe violations, including CSAM, targeted harassment, doxxing, or repeated rule-breaking after suspension, result in an immediate permanent ban with no appeal.',
  },
];

const faqs = [
  {
    q: 'Can I post a story I\'ve published elsewhere?',
    a: 'Yes, as long as you own the rights to it. Cross-posting from your personal blog or another platform is allowed, but you must disclose this in the submission notes. Stories must not violate any exclusive publishing agreements you may have signed.',
  },
  {
    q: 'What counts as "gratuitous gore"?',
    a: 'Gore that serves the narrative, building dread, showing consequence, reinforcing theme, is generally acceptable. Gore that exists solely for shock value with no story purpose crosses the line. If you\'re unsure, ask yourself: does removing this scene make the story weaker? If not, it\'s probably gratuitous.',
  },
  {
    q: 'How do I appeal a moderation decision?',
    a: 'If you believe a moderation action was applied incorrectly, you can submit an appeal through the Community forum\'s dedicated Appeals section. Include your username, the content in question, and why you believe the decision was incorrect. Appeals are reviewed by a senior moderator within 5 business days.',
  },
  {
    q: 'Are dark themes like death, suicide, or abuse allowed?',
    a: 'Horror fiction has always explored the darkest corners of human experience, and we don\'t shy away from that. These themes are permitted when handled with craft and narrative purpose. Content that glorifies, sensationalises, or provides instructional detail on self-harm is not permitted.',
  },
  {
    q: 'Can I write horror that\'s based on real events?',
    a: 'Inspired by real events is fine, horror has a long tradition of this. However, you may not write stories that name real, living individuals in a defamatory or harassing way, or stories designed to spread misinformation about real people.',
  },
  {
    q: 'My story was removed but I don\'t know why. What do I do?',
    a: 'You should have received a notification with the reason. If you didn\'t, or if the reason is unclear, reach out via the Community forum or contact our moderation team directly. We always aim to communicate removal reasons clearly.',
  },
];

const GuidelinesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const scrollPos = window.scrollY + 120;
      let current = 'overview';
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (el && el.offsetTop <= scrollPos) {
          current = s.id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    const timeout = setTimeout(() => {
      const el = sectionRefs.current[hash];
      if (el) {
        const offset = 90;
        const top = el.offsetTop - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        setActiveSection(hash);
        if (hash === 'faq') setOpenFaq(null);
      }
    }, 150);
    return () => clearTimeout(timeout);
  }, [location.hash]);

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      const offset = 90;
      const top = el.offsetTop - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const setRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Community Guidelines | Sinister Files Horror Fiction Platform"
        description="Read the Sinister Files community guidelines for writers and readers. Learn what content is allowed, how moderation works, and how to be part of our horror fiction community."
        canonical="https://sinisterfiles.com/guidelines"
      />
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/5920d930fda3f917d43b1e878941a09d.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-[#0a0a0a]" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto w-full">
          <p className="text-red-500 text-sm font-['Cinzel'] tracking-[0.3em] uppercase mb-4">
            The Law of the Dark
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
            Community <span className="text-red-600">Guidelines</span>
          </h1>
          <p className="text-gray-300 text-lg font-['Crimson_Text'] italic leading-relaxed">
            Even in the darkest places, there are rules. These are ours... written to protect the stories, the storytellers, and the souls brave enough to read them.
          </p>
          <p className="text-gray-500 text-sm font-['Crimson_Text'] mt-4">
            Last updated: March 2026
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex gap-10 items-start">

          {/* Sticky Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-[#0d0d0d] border border-red-900/20 rounded-lg p-6">
              <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase mb-5">
                Jump To
              </p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 cursor-pointer text-sm group whitespace-nowrap ${
                      activeSection === s.id
                        ? 'bg-red-600/15 text-red-400 border-l-2 border-red-600'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${s.icon} text-base`} />
                    </div>
                    {s.label}
                  </button>
                ))}
              </nav>

              <div className="mt-8 pt-6 border-t border-red-900/20">
                <p className="text-gray-500 text-xs font-['Crimson_Text'] leading-relaxed mb-4">
                  Have questions about the guidelines?
                </p>
                <Link
                  to="/community"
                  className="flex items-center gap-2 text-red-500 hover:text-red-400 text-xs font-['Cinzel'] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-discuss-line" />
                  Ask in the Forum
                </Link>
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0 space-y-20">

            {/* Overview */}
            <section ref={setRef('overview')} id="overview">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-compass-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 01</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Overview</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <div className="prose-dark space-y-5 font-['Crimson_Text'] text-gray-300 text-lg leading-relaxed">
                <p>
                  <strong className="text-white">Sinister Files</strong> is a community for horror fiction, a place where dark stories are told, read, debated, and celebrated. We believe horror is one of the most powerful literary forms, capable of revealing profound truths about fear, mortality, and the human condition.
                </p>
                <p>
                  These guidelines exist not to sanitise the darkness, but to ensure it serves a purpose. We have a zero-tolerance policy on content that causes real harm to real people, and a firm belief that the best horror doesn&apos;t need to cross those lines to be terrifying.
                </p>
                <p>
                  By using Sinister Files (whether as a reader, writer, or community member) you agree to abide by these guidelines. They apply to all content: stories, comments, forum posts, fan art, profile information, and direct messages.
                </p>
              </div>

              <div className="mt-8 bg-red-950/20 border border-red-900/30 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-red-400 text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-['Cinzel'] text-sm font-bold mb-2">These Guidelines Are a Living Document</p>
                    <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed">
                      As our community grows, so do these rules. We will update this page when significant changes are made and notify active members via the platform. The date at the top of this page reflects the last update.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Content Standards */}
            <section ref={setRef('content-standards')} id="content-standards">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-file-text-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 02</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Content Standards</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-10">
                Horror lives in extremes. We embrace that, but not everything belongs here. Below is a clear guide to what we welcome and what we won&apos;t allow.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Allowed */}
                <div className="bg-[#0d0d0d] border border-green-900/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-900/20 rounded-md">
                      <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                    </div>
                    <h3 className="text-white font-['Cinzel'] font-bold">Welcome Here</h3>
                  </div>
                  <ul className="space-y-3">
                    {allowedContent.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-green-500 text-sm" />
                        </div>
                        <span className="text-gray-300 font-['Crimson_Text'] text-base leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Prohibited */}
                <div className="bg-[#0d0d0d] border border-red-900/30 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-900/20 rounded-md">
                      <i className="ri-close-circle-line text-red-500 text-lg" />
                    </div>
                    <h3 className="text-white font-['Cinzel'] font-bold">Not Permitted</h3>
                  </div>
                  <ul className="space-y-3">
                    {prohibitedContent.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-close-line text-red-500 text-sm" />
                        </div>
                        <span className="text-gray-300 font-['Crimson_Text'] text-base leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Writer Rules */}
            <section ref={setRef('writer-rules')} id="writer-rules">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-quill-pen-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 03</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Writer Rules</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-10">
                These rules apply specifically to story submissions. Writers bear extra responsibility, your work is the foundation everything else is built on.
              </p>

              <div className="grid md:grid-cols-2 gap-5">
                {writerRules.map((rule) => (
                  <div
                    key={rule.title}
                    className="bg-[#0d0d0d] border border-red-900/20 rounded-lg p-6 hover:border-red-600/40 transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg flex-shrink-0 group-hover:bg-red-600/20 transition-colors">
                        <i className={`${rule.icon} text-red-500 text-lg`} />
                      </div>
                      <div>
                        <h3 className="text-white font-['Cinzel'] font-bold text-base mb-2">{rule.title}</h3>
                        <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed">{rule.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-center">
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-3 px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-quill-pen-line" />
                  Submit a Story
                </Link>
              </div>
            </section>

            {/* Community Conduct */}
            <section ref={setRef('community-conduct')} id="community-conduct">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-group-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 04</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Community Conduct</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-10">
                How you interact with other members matters as much as what you post. The Dark Congregation is a community, treat it like one.
              </p>

              <div className="space-y-5">
                {conductRules.map((group) => {
                  const borderColor =
                    group.level === 'green'
                      ? 'border-green-900/30'
                      : group.level === 'yellow'
                      ? 'border-yellow-900/30'
                      : 'border-red-900/30';
                  const iconColor =
                    group.level === 'green'
                      ? 'text-green-500 bg-green-900/20'
                      : group.level === 'yellow'
                      ? 'text-yellow-500 bg-yellow-900/20'
                      : 'text-red-500 bg-red-900/20';
                  const dotColor =
                    group.level === 'green'
                      ? 'bg-green-500'
                      : group.level === 'yellow'
                      ? 'bg-yellow-500'
                      : 'bg-red-500';

                  return (
                    <div key={group.title} className={`bg-[#0d0d0d] border ${borderColor} rounded-lg p-6`}>
                      <div className="flex items-center gap-3 mb-5">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-md ${iconColor}`}>
                          <i className={`${group.icon} text-lg`} />
                        </div>
                        <h3 className="text-white font-['Cinzel'] font-bold text-base">{group.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {group.items.map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2 ${dotColor}`} />
                            <span className="text-gray-300 font-['Crimson_Text'] text-base leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Moderation */}
            <section ref={setRef('moderation')} id="moderation">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-shield-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 05</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Moderation</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-12">
                Violations of these guidelines are handled through a tiered system. We aim to be fair, transparent, and consistent. Moderators are volunteers from the community who are held to the same standards as everyone else.
              </p>

              <div className="relative">
                {/* Connector line */}
                <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-red-900/30 hidden md:block" />

                <div className="space-y-6">
                  {moderationSteps.map((step) => (
                    <div key={step.step} className="flex gap-6 items-start">
                      <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#111] border border-red-900/30 rounded-lg relative z-10">
                        <span className="text-red-600 font-['Cinzel'] font-bold text-lg">{step.step}</span>
                      </div>
                      <div className="flex-1 bg-[#0d0d0d] border border-red-900/20 rounded-lg p-6 hover:border-red-600/30 transition-colors">
                        <h3 className="text-white font-['Cinzel'] font-bold text-lg mb-3">{step.title}</h3>
                        <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 bg-[#111] border border-red-900/20 rounded-lg p-6">
                <h4 className="text-white font-['Cinzel'] font-bold mb-3">Reporting Content</h4>
                <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed">
                  Every story, comment, and forum post has a report button. Use it. Reports go directly to our moderation team and are reviewed within 48 hours. False or malicious reporting to harm other members is itself a guideline violation.
                </p>
              </div>
            </section>

            {/* FAQ */}
            <section ref={setRef('faq')} id="faq">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-red-600/10 rounded-lg">
                  <i className="ri-question-line text-red-500 text-xl" />
                </div>
                <div>
                  <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.25em] uppercase">Section 06</p>
                  <h2 className="text-3xl font-bold text-white font-['Cinzel']">Frequently Asked</h2>
                </div>
              </div>
              <div className="w-12 h-0.5 bg-red-600 mb-8" />

              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className={`bg-[#0d0d0d] border rounded-lg overflow-hidden transition-all duration-300 ${
                      openFaq === idx ? 'border-red-600/40' : 'border-red-900/20 hover:border-red-900/40'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span className="text-white font-['Cinzel'] font-bold text-base">{faq.q}</span>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`ri-add-line text-red-500 text-xl transition-transform duration-300 ${
                            openFaq === idx ? 'rotate-45' : ''
                          }`}
                        />
                      </div>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6 border-t border-red-900/20">
                        <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed pt-4">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

          </main>
        </div>
      </div>

      {/* CTA */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=dark%20gothic%20cathedral%20interior%20with%20stained%20glass%20windows%20casting%20deep%20crimson%20and%20shadow%20patterns%20across%20stone%20floor%20ancient%20iron%20chandeliers%20with%20black%20candles%20atmospheric%20horror%20aesthetic%20fog%20drifting%20through%20vaulted%20arches%20cinematic%20moody&width=1440&height=600&seq=guidelines-cta-01&orientation=landscape)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black/65 to-[#0a0a0a]" />
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <i className="ri-scales-3-line text-red-600 text-5xl mb-6 block" />
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
            You Know the Rules
          </h2>
          <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-10">
            Now step into the darkness. Read something that terrifies you, or write something that will terrify others. The Congregation awaits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/stories"
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Browse Stories
            </Link>
            <Link
              to="/community"
              className="px-8 py-3 border border-red-600/50 hover:border-red-600 text-red-400 hover:text-red-300 font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Join the Community
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidelinesPage;
